import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiResponse } from '../models/contract.model';
import {
  TestRunFlatRequest,
  TestRunFlatResponse,
  TestRunLineItemRequest,
  TestRunLineItemRow,
  TestRunLineItemStatus,
  TestRunPagedRequest,
  TestRunPagedResponse,
  TestRunProvisionRequest,
  TestRunProvisionRow,
  TestRunScheduleDetectionResponse,
  TestRunStoreJobDetail,
  TestRunXrRow,
} from '../models/test-run-line-item.model';

export const PHYSICIAN_CONTRACTS_API_ROOT = new InjectionToken<string>('PHYSICIAN_CONTRACTS_API_ROOT');
// Separate from PHYSICIAN_CONTRACTS_API_ROOT because the TestRunStore status endpoint
// lives on pricing-workflow-api (it owns the Databricks orchestration state).
export const PRICING_WORKFLOW_API_ROOT = new InjectionToken<string>('PRICING_WORKFLOW_API_ROOT');

@Injectable({ providedIn: 'root' })
export class TestRunLineItemsService {
  constructor(
    private readonly http: HttpClient,
    @Inject(PHYSICIAN_CONTRACTS_API_ROOT) private readonly apiRoot: string,
    @Optional() @Inject(PRICING_WORKFLOW_API_ROOT) private readonly workflowApiRoot: string | null = null
  ) {}

  getStatus(parentJobId: string, healthSystemId: string): Observable<TestRunLineItemStatus> {
    const url = `${this.apiRoot}/test-run/${encodeURIComponent(parentJobId)}/line-items/status`;
    const params = new HttpParams().set('healthSystemId', healthSystemId);
    return this.http
      .get<ApiResponse<TestRunLineItemStatus>>(url, { params })
      .pipe(map((res) => res.data as TestRunLineItemStatus));
  }

  // Returns the Databricks store-run state for a given parent job. Hits the pricing-workflow-api
  // directly since it owns the orchestration row. The endpoint responds 404 when no row exists
  // yet — caller treats that as "not triggered yet" rather than an error.
  getStoreStatus(parentJobId: string): Observable<TestRunStoreJobDetail> {
    const url = `${this.workflowApiRoot}/test-run-store/${encodeURIComponent(parentJobId)}`;
    return this.http.get<TestRunStoreJobDetail>(url);
  }

  // POST. Body carries paging + sortModel + filterModel — same shape as flat.
  getXrRows(parentJobId: string, request: TestRunPagedRequest): Observable<TestRunPagedResponse<TestRunXrRow>> {
    const url = `${this.apiRoot}/test-run/${encodeURIComponent(parentJobId)}/line-items/xr`;
    return this.http
      .post<ApiResponse<TestRunPagedResponse<TestRunXrRow>>>(url, request)
      .pipe(map((res) => res.data as TestRunPagedResponse<TestRunXrRow>));
  }

  getProvisionRows(parentJobId: string, request: TestRunProvisionRequest): Observable<TestRunPagedResponse<TestRunProvisionRow>> {
    const url = `${this.apiRoot}/test-run/${encodeURIComponent(parentJobId)}/line-items/provision`;
    return this.http
      .post<ApiResponse<TestRunPagedResponse<TestRunProvisionRow>>>(url, request)
      .pipe(map((res) => res.data as TestRunPagedResponse<TestRunProvisionRow>));
  }

  getLineItemRows(parentJobId: string, request: TestRunLineItemRequest): Observable<TestRunPagedResponse<TestRunLineItemRow>> {
    const url = `${this.apiRoot}/test-run/${encodeURIComponent(parentJobId)}/line-items/lineitem`;
    return this.http
      .post<ApiResponse<TestRunPagedResponse<TestRunLineItemRow>>>(url, request)
      .pipe(map((res) => res.data as TestRunPagedResponse<TestRunLineItemRow>));
  }

  getFlatLineItemRows(parentJobId: string, request: TestRunFlatRequest): Observable<TestRunFlatResponse> {
    const url = `${this.apiRoot}/test-run/${encodeURIComponent(parentJobId)}/line-items/flat`;
    return this.http
      .post<ApiResponse<TestRunFlatResponse>>(url, request)
      .pipe(map((res) => res.data as TestRunFlatResponse));
  }

  // Schedule detection, one row per probe. Same paging/sort/filter request shape as flat;
  // the server explodes the probes array and left-joins charge/XR context.
  getScheduleDetectionRows(parentJobId: string, request: TestRunPagedRequest): Observable<TestRunScheduleDetectionResponse> {
    const url = `${this.apiRoot}/test-run/${encodeURIComponent(parentJobId)}/line-items/schedule-detection`;
    return this.http
      .post<ApiResponse<TestRunScheduleDetectionResponse>>(url, request)
      .pipe(map((res) => res.data as TestRunScheduleDetectionResponse));
  }
}
